import { useEffect } from "react";

function getCardAreas(card) {
    return (card.getAttribute("data-area") || "").split(/\s+/).filter(Boolean);
}

export default function useCatalogFilter(
    ref,
    {
        initialType = "posgrado",
        initialArea = "all",
        scrollTargetId = null,
        scrollExtraOffset = 0,
    } = {},
) {
    useEffect(() => {
        const container = ref.current;

        if (!container) {
            return undefined;
        }

        const tabs = [...container.querySelectorAll(".cat-tab")];
        const filters = [...container.querySelectorAll(".cat-filter")];
        const cards = [...container.querySelectorAll(".c-card")];

        if (!tabs.length || !filters.length || !cards.length) {
            return undefined;
        }

        const availableTypes = new Set(tabs.map((tab) => tab.getAttribute("data-filter")).filter(Boolean));
        const availableAreas = new Set(filters.map((filter) => filter.getAttribute("data-area")).filter(Boolean));

        let activeType = availableTypes.has(initialType)
            ? initialType
            : tabs.find((tab) => tab.classList.contains("active"))?.getAttribute("data-filter") || "posgrado";
        let activeArea = availableAreas.has(initialArea)
            ? initialArea
            : filters.find((filter) => filter.classList.contains("active"))?.getAttribute("data-area") || "all";

        const getAvailableAreasForType = () => {
            const nextAreas = new Set(["all"]);

            cards.forEach((card) => {
                const cardType = card.getAttribute("data-type");

                if (cardType !== activeType) {
                    return;
                }

                getCardAreas(card).forEach((area) => nextAreas.add(area));
            });

            return nextAreas;
        };

        const syncActiveState = () => {
            const enabledAreas = getAvailableAreasForType();

            if (!enabledAreas.has(activeArea)) {
                activeArea = "all";
            }

            tabs.forEach((tab) => {
                const tabType = tab.getAttribute("data-filter") || "posgrado";
                tab.classList.toggle("active", tabType === activeType);
            });

            filters.forEach((filter) => {
                const filterArea = filter.getAttribute("data-area") || "all";
                const isDisabled = !enabledAreas.has(filterArea);

                filter.classList.toggle("active", filterArea === activeArea);
                filter.classList.toggle("is-disabled", isDisabled);
                filter.setAttribute("aria-disabled", isDisabled ? "true" : "false");
                filter.tabIndex = isDisabled ? -1 : 0;
            });
        };

        const applyVisibility = () => {
            syncActiveState();

            cards.forEach((card) => {
                const cardType = card.getAttribute("data-type");
                const cardAreas = getCardAreas(card);
                const showType = cardType === activeType;
                const showArea = activeArea === "all" || cardAreas.includes(activeArea);
                card.style.display = showType && showArea ? "" : "none";
            });
        };

        const handlers = [];

        tabs.forEach((tab) => {
            const handler = () => {
                activeType = tab.getAttribute("data-filter") || "posgrado";
                applyVisibility();
            };

            tab.addEventListener("click", handler);
            handlers.push([tab, handler]);
        });

        filters.forEach((filter) => {
            const handler = () => {
                if (filter.classList.contains("is-disabled")) {
                    return;
                }

                activeArea = filter.getAttribute("data-area") || "all";
                applyVisibility();
            };

            filter.addEventListener("click", handler);
            handlers.push([filter, handler]);
        });

        applyVisibility();

        let scrollTimeoutId;

        if (scrollTargetId) {
            const target = container.querySelector(`#${scrollTargetId}`) || document.getElementById(scrollTargetId);

            if (target) {
                scrollTimeoutId = window.setTimeout(() => {
                    const targetStyles = window.getComputedStyle(target);
                    const scrollMarginTop = Number.parseFloat(targetStyles.scrollMarginTop) || 0;
                    const responsiveExtraOffset = scrollExtraOffset
                        ? Math.min(scrollExtraOffset, window.innerWidth < 768 ? 120 : scrollExtraOffset)
                        : 0;
                    const nextTop = Math.max(
                        0,
                        target.getBoundingClientRect().top + window.scrollY - scrollMarginTop + responsiveExtraOffset,
                    );

                    window.scrollTo({
                        top: nextTop,
                        behavior: "smooth",
                    });
                }, 120);
            }
        }

        return () => {
            handlers.forEach(([element, handler]) => {
                element.removeEventListener("click", handler);
            });

            if (scrollTimeoutId) {
                window.clearTimeout(scrollTimeoutId);
            }
        };
    }, [initialArea, initialType, ref, scrollExtraOffset, scrollTargetId]);
}
